import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Skull, Gem, HelpCircle } from "lucide-react";

const DIFFS = [
  { id: "easy", label: "EASY", note: "4 tiles · 1 trap", cls: "text-neon-green border-neon-green" },
  { id: "medium", label: "MEDIUM", note: "3 tiles · 1 trap", cls: "text-neon-cyan border-neon-cyan" },
  { id: "hard", label: "HARD", note: "2 tiles · 1 trap", cls: "text-neon-red border-neon-red" },
];

export default function Tower() {
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [session, setSession] = useState(null);
  const [amount, setAmount] = useState("5");
  const [diff, setDiff] = useState("easy");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    api.get("/tower/active").then(({ data }) => { if (data.session) setSession(data.session); }).catch(() => {});
  }, []);

  const apply = (data) => {
    setSession(data);
    if (data.balance_usdt !== undefined) setWallet((w) => ({ ...w, balance_usdt: data.balance_usdt }));
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tower/start", { amount: parseFloat(amount), difficulty: diff });
      apply(data);
    } catch (err) {
      toast.error(apiError(err, "Failed to start"));
    } finally { setBusy(false); }
  };

  const pick = async (tile) => {
    if (!session || session.status !== "active" || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tower/pick", { tile });
      apply(data);
      if (data.status === "lost") toast.error("TRAP! Tower collapsed — entry lost.");
      else if (data.status === "won") toast.success(`TOP FLOOR! +${data.win.toFixed(2)} USDT`);
    } catch (err) {
      toast.error(apiError(err, "Pick failed"));
    } finally { setBusy(false); }
  };

  const cashout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tower/cashout");
      apply(data);
      toast.success(`Cashed out +${data.win.toFixed(2)} USDT at ${data.current_multiplier}×`);
    } catch (err) {
      toast.error(apiError(err, "Cashout failed"));
    } finally { setBusy(false); }
  };

  const active = session?.status === "active";
  const ended = session && session.status !== "active";

  return (
    <div className="min-h-[calc(100vh-4rem)] relative scanlines" data-testid="tower-page">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 relative z-10">
        <div className="lg:col-span-3 space-y-4">
          <div className="panel p-6" data-testid="tower-board">
            <div className="flex items-center justify-between mb-5">
              <div className="label-mono flex items-center gap-2"><span className="dot-live" /> TOWER CLIMB</div>
              <div className="label-mono text-white/40">{session ? `${session.difficulty} · ${session.tiles} tiles` : "8 floors · provably fair"}</div>
            </div>

            {!session && (
              <div className="min-h-[380px] grid place-items-center text-center">
                <div>
                  <div className="font-display font-black text-4xl lg:text-5xl tracking-tighter">CLIMB THE <span className="text-neon-cyan glow-cyan">TOWER</span>.</div>
                  <p className="text-white/50 font-body mt-3 max-w-md mx-auto">Pick the safe tile on each floor. Every floor multiplies your entry — cash out any time, or hit a trap and lose it all.</p>
                  <div className="label-mono mt-5">set your entry on the right →</div>
                </div>
              </div>
            )}

            {session && (
              <div className="space-y-2" data-testid="tower-grid">
                {Array.from({ length: session.levels }, (_, i) => session.levels - 1 - i).map((lv) => {
                  const pickAt = session.picks.find((p) => p.level === lv);
                  const isCurrent = active && lv === session.level;
                  const passed = lv < session.level;
                  const trap = session.traps ? session.traps[lv] : null;
                  return (
                    <div key={lv} className={`flex items-center gap-3 ${isCurrent ? "" : passed ? "opacity-90" : "opacity-40"}`} data-testid={`tower-row-${lv}`}>
                      <div className={`w-16 text-right font-mono text-xs ${isCurrent ? "text-neon-cyan" : passed ? "text-neon-green" : "text-white/40"}`}>
                        {session.multipliers[lv].toFixed(2)}×
                      </div>
                      <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${session.tiles}, 1fr)` }}>
                        {Array.from({ length: session.tiles }, (_, tile) => {
                          const chosen = pickAt?.tile === tile;
                          const isTrap = trap !== null && trap === tile;
                          let cls = "border-neon-line bg-black/40 text-white/30";
                          if (chosen && pickAt.safe) cls = "border-neon-green bg-neon-green/10 text-neon-green";
                          else if (chosen && !pickAt.safe) cls = "border-neon-red bg-neon-red/20 text-neon-red";
                          else if (ended && isTrap) cls = "border-neon-red/60 bg-black/40 text-neon-red/70";
                          else if (isCurrent) cls = "border-neon-cyan/60 bg-neon-cyan/5 text-neon-cyan hover:bg-neon-cyan/15 cursor-pointer";
                          return (
                            <button
                              key={tile}
                              disabled={!isCurrent || busy}
                              onClick={() => pick(tile)}
                              className={`h-11 border grid place-items-center transition-colors ${cls}`}
                              data-testid={`tower-tile-${lv}-${tile}`}
                            >
                              {chosen && pickAt.safe && <Gem className="w-4 h-4" />}
                              {((chosen && !pickAt.safe) || (ended && isTrap && !chosen)) && <Skull className="w-4 h-4" />}
                              {!chosen && !(ended && isTrap) && (isCurrent ? <HelpCircle className="w-4 h-4" /> : null)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {ended && (
              <div className="mt-5 flex items-center justify-between border-t border-neon-line pt-4" data-testid="tower-result-bar">
                <div className={`font-display font-black text-xl ${session.win > 0 ? "text-neon-green glow-green" : "text-neon-red glow-red"}`}>
                  {session.status === "lost" ? "TOWER COLLAPSED" : session.status === "won" ? `TOP FLOOR · +${session.win.toFixed(2)} USDT` : `CASHED OUT · +${session.win.toFixed(2)} USDT`}
                </div>
                <button onClick={() => setSession(null)} className="btn-neon !py-2 !px-4 text-xs" data-testid="tower-play-again-button">PLAY AGAIN</button>
              </div>
            )}
          </div>

          <div className="panel p-5" data-testid="tower-how-to">
            <div className="label-mono mb-4">// how_to_play</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border border-neon-cyan/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-cyan">01</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Set entry & difficulty</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">Easy has 4 tiles per floor, Hard only 2 — but harder floors multiply much faster.</p>
              </div>
              <div className="border border-neon-green/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-green">02</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Pick the safe tile</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">One trap hides on every floor. Choose a safe tile to climb and grow your multiplier.</p>
              </div>
              <div className="border border-neon-red/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-red">03</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Cash out or climb</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">Bank your win at any floor — or push for the top. Hit a trap and the entry is gone.</p>
              </div>
            </div>
          </div>

          {ended && session.server_seed && (
            <div className="panel p-4 font-mono text-xs space-y-1.5" data-testid="tower-fairness">
              <div className="label-mono mb-2">// provably_fair · this tower</div>
              <div className="text-white/40">seed hash: <span className="text-neon-cyan break-all">{session.server_seed_hash}</span></div>
              <div className="text-white/40">seed: <span className="text-white/70 break-all">{session.server_seed}</span></div>
              <div className="text-white/40">trap(floor) = int(SHA-256(seed:floor)[0:8], 16) mod tiles</div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="panel p-5" data-testid="tower-wallet-mini">
            <div className="label-mono">Wallet</div>
            <div className="font-mono text-2xl font-bold mt-1 text-neon-green glow-green" data-testid="tower-wallet-balance">
              {wallet.balance_usdt?.toFixed(2)} <span className="text-xs text-white/50">USDT</span>
            </div>
          </div>

          {!active && (
            <div className="panel p-5 space-y-4" data-testid="tower-entry-panel">
              <div className="label-mono">Entry Panel</div>
              <div>
                <label className="label-mono">Amount (USDT) · min 5</label>
                <input type="number" min="5" step="1" className="field mt-2" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tower-amount-input" />
                <div className="grid grid-cols-4 gap-1.5 mt-2">
                  {[5, 10, 25, 50].map((v) => (
                    <button key={v} className="panel py-1.5 text-xs font-mono hover:border-neon-green transition-colors" onClick={() => setAmount(String(v))} data-testid={`tower-quick-${v}`}>{v}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-mono">Difficulty</label>
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  {DIFFS.map((d) => (
                    <button key={d.id} onClick={() => setDiff(d.id)} className={`border py-2 text-[10px] font-mono uppercase tracking-widest transition-colors ${diff === d.id ? d.cls : "border-neon-line text-white/40 hover:text-white"}`} data-testid={`tower-diff-${d.id}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="text-white/40 text-[10px] font-mono mt-1.5">{DIFFS.find((d) => d.id === diff)?.note}</div>
              </div>
              <button onClick={start} disabled={busy || (parseFloat(amount) || 0) < 5} className="btn-neon w-full" data-testid="tower-start-button">
                {busy ? "…" : "START TOWER"}
              </button>
            </div>
          )}

          {active && (
            <div className="panel p-5 space-y-4" data-testid="tower-live-panel">
              <div className="label-mono">Live Tower</div>
              <div className="font-mono text-xs space-y-1 text-white/50">
                <div className="flex justify-between"><span>Entry</span><span className="text-white">{session.amount.toFixed(2)} USDT</span></div>
                <div className="flex justify-between"><span>Floor</span><span className="text-neon-cyan">{session.level} / {session.levels}</span></div>
                <div className="flex justify-between"><span>Current</span><span className="text-neon-green">{session.current_multiplier.toFixed(2)}×</span></div>
                <div className="flex justify-between"><span>Next floor</span><span className="text-neon-cyan">{session.multipliers[session.level]?.toFixed(2)}×</span></div>
              </div>
              <button onClick={cashout} disabled={busy || session.level < 1} className="btn-neon w-full" data-testid="tower-cashout-button">
                {session.level < 1 ? "PICK A TILE TO CLIMB" : `CASH OUT ${(session.amount * session.current_multiplier).toFixed(2)} USDT`}
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
